import { MutableRefObject, useEffect } from 'react';
import { ValidationErrorMessage, ValidationErrorMessageWithArgs } from 'react-admin';
import { useFormContext } from 'react-hook-form';

/**
 * Shared handling for a Laravel 422 that names a field the form does not render.
 *
 * Since TVR-1915 `src/providers/data/index.ts` translates Laravel's `{ errors: { field: [...] } }`
 * into the flat `{ field: string }` shape react-admin's `setSubmissionErrors` understands, so the
 * *field-bound* half of a 422 needs no code in a form at all. What the provider cannot do is know
 * which fields have a mounted input — it never sees the JSX — and on a `mutationMode="pessimistic"`
 * Edit an error set on a field with no input is worse than invisible:
 *
 *   - `useEditController` suppresses its own notification for a validation error in pessimistic
 *     mode, so nothing is shown;
 *   - `setError` writes into `formState.errors` at a path that no registered field owns, and
 *     react-hook-form's `executeBuiltInValidation` only ever clears errors for names present in
 *     `_fields`. The entry therefore survives every later submit, and `handleSubmit`'s
 *     `isEmptyObject(_formState.errors)` gate never passes again — the form is wedged until it is
 *     remounted.
 *
 * So a form whose API can 422 on a field it does not render has to partition the response itself
 * and show the unbound half somewhere of its own. TVR-1909 worked that out in
 * `src/resources/software-releases/SoftwareReleaseEdit.tsx`; TVR-2141 lifted it here when
 * `src/resources/commands/CommandEdit.tsx` turned out to need exactly the same thing.
 *
 * The alert that renders `formErrors` stays in each form: its title is resource-specific, and where
 * it belongs on the page is a layout decision, not a shared one.
 */

/**
 * react-admin's transport format for an error that carries interpolation arguments.
 *
 * Module-private on purpose, as are `serverError` and `toFieldError` below: `partitionServerErrors`,
 * `MountedFieldsProbe` and `MountedFieldsReader` are the whole public surface of this module, and the
 * three helpers are reached — and covered — through it, not imported anywhere else.
 *
 * The same literal is written out once more in `src/providers/data/index.ts`, which builds this
 * format when it translates a Laravel 422. The two are not shared and should not be: importing it
 * there would point the data provider at a component module, which is the wrong direction, and
 * react-admin keeps its own copy private too (`ValidationErrorSpecialFormatPrefix` in
 * `ra-core/src/form/validation/ValidationError.tsx` is not exported), so there is no neutral owner to
 * put it in. It is react-admin's constant, not ours — if it ever changes, both copies change.
 */
const VALIDATION_ERROR_PREFIX = '@@react-admin@@';

/** Reads the currently registered field names at the moment it is called — see `MountedFieldsProbe`. */
export type MountedFieldsReader = (() => Set<string>) | undefined;

/**
 * Reads one entry of a 422 body as something react-admin can render.
 *
 * The provider hands back one **string** per field — a plain message, or react-admin's
 * `@@react-admin@@{"message","args"}` transport format when the API attached interpolation
 * arguments. Both pass through here untouched, and `<ValidationError>` decodes the second form when
 * it renders. The array and object branches are kept because they cost a handful of lines and are
 * the only thing standing between an unreviewed provider change and a silently blank error.
 */
const serverError = (value: unknown): ValidationErrorMessage | undefined => {
  if (typeof value === 'string') {
    return value.length > 0 ? value : undefined;
  }

  /** Defensive: the provider hands back one entry per field, but Laravel itself sends a list. */
  if (Array.isArray(value)) {
    return value.map(serverError).find((error) => error != null);
  }

  if (value == null || typeof value !== 'object' || !('message' in value)) {
    return undefined;
  }

  const { message, args } = value as { message: unknown; args?: unknown };
  if (typeof message !== 'string' || message.length === 0) {
    return undefined;
  }

  return args == null ? message : { message, args: args as ValidationErrorMessageWithArgs['args'] };
};

/**
 * react-hook-form stores an error message as a plain string, so an error with args travels in
 * react-admin's own escape hatch for exactly this — the format `useInput` emits for its
 * validators, and `ValidationError` parses back before translating.
 */
const toFieldError = (error: ValidationErrorMessage): string =>
  typeof error === 'string' ? error : `${VALIDATION_ERROR_PREFIX}${JSON.stringify(error)}`;

/**
 * Splits the server's field errors into the ones an input can show (returned to react-admin, which
 * sets them on the input) and the ones that belong to no input, which the caller shows in an alert
 * instead of losing.
 *
 * Which fields have an input is asked of react-hook-form's registry of mounted fields rather than
 * kept in a list per form: a list would have to be edited in step with the JSX, and nothing — not
 * the compiler, not a test — would notice if it were not.
 *
 * Laravel names a nested field with a dotted path (`feedback_config.wait_type`), which is the same
 * string react-admin passes as an input's `source` and react-hook-form stores flat in
 * `_names.mount`. So the lookup below is a plain `Set.has` on the dotted key, with no splitting: a
 * nested field routes to its input exactly like a top-level one, and an error on the *container*
 * (`feedback_config` itself) is unbound, because no input is registered under that name.
 */
export const partitionServerErrors = (
  raw: unknown,
  hasInput: (field: string) => boolean,
): { fieldErrors: Record<string, string>; formErrors: ValidationErrorMessage[] } => {
  const fieldErrors: Record<string, string> = {};
  const formErrors: ValidationErrorMessage[] = [];

  if (raw == null || typeof raw !== 'object') {
    return { fieldErrors, formErrors };
  }

  Object.entries(raw as Record<string, unknown>).forEach(([field, value]) => {
    const error = serverError(value);
    if (error == null) {
      return;
    }

    if (hasInput(field)) {
      fieldErrors[field] = toFieldError(error);
    } else {
      formErrors.push(error);
    }
  });

  return { fieldErrors, formErrors };
};

/**
 * Publishes react-hook-form's set of mounted field names to the submit handler, which runs outside
 * the form provider and so has no way to ask for it. Renders nothing; drop it anywhere inside the
 * form.
 *
 * It publishes a *getter*, never the Set: `reset()` replaces `_names` wholesale, and `ra-core`'s
 * `useAugmentedForm` resets from an effect in the parent `Form` — which runs after this child's
 * effect. A captured Set would therefore be the pre-reset one, holding only the fields registered
 * in the first commit, and every input that mounts later (a conditional section, a select whose
 * choices are still loading) would look like a field with no input. `control` itself survives the
 * reset, so the read has to happen when the handler runs. Found the hard way under TVR-1909.
 */
export const MountedFieldsProbe = ({ mountedFields }: { mountedFields: MutableRefObject<MountedFieldsReader> }) => {
  const { control } = useFormContext();

  useEffect(() => {
    mountedFields.current = () => control._names.mount;
  }, [control, mountedFields]);

  return null;
};
